import type { ListColumn } from "../../../../../shared/frontend/list/listColumn";
import { Alert, Button, Checkbox, Form, Grid, Input, InputNumber, Radio, Select, Space, Steps, Typography } from "../../../../../shared/frontend/ui";
import { DatePicker } from "../../../../../shared/frontend/components/ControlledPickers";
import dayjs,{ Dayjs } from "dayjs";
import { useQuery } from "@tanstack/react-query";
import React from "react";
import { useFormDirty } from "../app/utils/useFormDirty";
import { useDirtySnapshot } from "../../../../../shared/frontend/forms/useDirtySnapshot";
import { useAuth } from "../app/auth/useAuth";
import { useFinanceOperation } from "../app/operations/useFinanceOperation";
import { spacing, cssSpacing } from "../app/design-system";
import { useI18n } from "../app/i18n/useI18n";
import { useDateInputFormat } from "../app/units/DateInputFormatContext";
import { decimalSeparatorOf,formatDateOnly,formatMoney,moneyPlaceholderOf } from "../app/units/format";
import { loadUnits } from "../app/units/unitsStorage";
import { chargebackReasonBlocksFutureDebits, getChargebackReasonOptions } from "../app/utils/chargebackReason";
import {
  CANCELLATION_REASON_TYPE_CHARGEBACK,
  CANCELLATION_REASON_TYPE_REFUND
} from "../app/utils/paymentCancellationReason";
import { paymentMethodDefinitionSelectOptions,paymentMethodLabel } from "../app/utils/paymentMethod";
import { formatDateOnlyIso } from "../app/utils/formatDateOnlyIso";
import { isValidIban, normalizeIban } from "../app/utils/sepa";
import { PAYMENT_METHOD_TRANSFER } from "../app/utils/paymentMethodCodes";
import { DrawerSelectionList } from "./DrawerSelectionList";
import { FormDrawer } from "./FormDrawer";
import type { ReminderCaseTrigger } from "./ReminderCreateDrawer";
import { useFeedback } from "../../../../../shared/frontend/feedback/useFeedback";
import { ISO_DATE_LENGTH } from "../app/utils/isoDate";

/** rowKind der Inkassoposten in der Zahlungsliste (nicht stornierbar). */
const ROW_KIND_DEBT_COLLECTION = "debtCollection";
// Spaltenbreiten der Auswahlliste
const DATE_COLUMN_WIDTH = 112;
const REFERENCE_COLUMN_WIDTH = 280;
const AMOUNT_COLUMN_WIDTH = 112;
// Umgang mit Rücklastschriften (Backend: ChargebackHandlingMode)
const CHARGEBACK_HANDLING_NEXT_RUN = 0;
const CHARGEBACK_HANDLING_BLOCK_UNTIL_PAID = 1;
const CHARGEBACK_HANDLING_BLOCK_RETURNED_ONLY = 2;

export type PaymentCancellationItem = {
  id: string;
  /** Verbleibend stornierbarer Betrag (Server: voidableRemaining). */
  voidableRemaining: number;
  /** Ursprünglicher Buchungsbetrag der Zeile. */
  originalAmount: number;
  paidDate?: string | null;
  method?: string | null;
  reference?: string | null;
};

export type PaymentCancellationDrawerProps = {
  open: boolean;
  /** Kontoauszug: Die aktuelle Personenzuordnung wird noch geladen. */
  contentLoading?: boolean;
  /** Einzelne Zahlung (Legacy). */
  payment?: PaymentCancellationItem;
  /** Kandidaten für Stornierung. Wenn leer/undefined, lädt der Drawer die Zahlungen selbst (GET person payments, nicht storniert). */
  payments?: PaymentCancellationItem[];
  onClose: () => void;
  onSuccess?: (result: PaymentCancellationResult) => void | Promise<void>;
  operationResources?: readonly string[];
  personId: string;
  /** Kontoauszug: ausschließlich eine Rücklastschrift buchen, ohne Wahl der Stornoart. */
  chargebackOnly?: boolean;
  /**
   * Vorbelegung beim Öffnen, z. B. aus einem Kontoauszug: Rücklastschrift mit Rückgabegrund und Buchungsdatum.
   * Wirkt einmal je Öffnen, wie die Vorauswahl der Zahlungen.
   */
  initialValues?: { art?: number; chargebackReasonCode?: string | null; cancellationDate?: string | null; cancellationAmount?: number };
  /** Personenkontext über der Schrittleiste, in allen Schritten sichtbar. */
  header?: React.ReactNode;
};

export type PaymentCancellationResult = {
  cancelledCount: number;
  personId: string;
  affectedOpenItemIds: string[];
  reminderCases: ReminderCaseTrigger[];
  /** Neu angelegte Überweisungsaufträge (Rückerstattung per Überweisung). */
  transferOrderIds?: string[];
};

type ApiPaymentRow = {
  id: string;
  amount: number;
  paidDate?: string | null;
  method?: string | null;
  reference?: string | null;
  isCancelled?: boolean;
  reversesPaymentId?: string | null;
  canVoid?: boolean;
  voidableRemaining?: number | null;
  rowKind?: string | null;
};

type CancellationReason = { id: string; name: string; reasonType: number };
const CUSTOM_REASON_ID = "custom";

function seedValues(initialValues: PaymentCancellationDrawerProps["initialValues"], chargebackOnly: boolean) {
  return {
    art: chargebackOnly ? CANCELLATION_REASON_TYPE_CHARGEBACK : initialValues?.art ?? CANCELLATION_REASON_TYPE_REFUND,
    cancellationAmount: initialValues?.cancellationAmount,
    cancellationDate: initialValues?.cancellationDate ? dayjs(initialValues.cancellationDate).startOf("day") : dayjs().startOf("day"),
    ...(initialValues?.chargebackReasonCode ? { chargebackReasonCode: initialValues.chargebackReasonCode } : {})
  };
}

export const PaymentCancellationDrawer: React.FC<PaymentCancellationDrawerProps> = ({
  open,
  contentLoading = false,
  payment: paymentProp,
  payments: paymentsProp,
  onClose,
  onSuccess,
  operationResources,
  personId,
  chargebackOnly = false,
  initialValues,
  header
}) => {
  const feedback = useFeedback();
  const auth = useAuth();
  const { lang, text } = useI18n();
  const chargebackReasonOptions = React.useMemo(() => getChargebackReasonOptions(text, lang), [text, lang]);
  const units = loadUnits();
  const dateInputFormat = useDateInputFormat();
  const screens = Grid.useBreakpoint();
  const stackPaymentFields = screens.sm === false;
  const decimalSeparator = decimalSeparatorOf(units);
  const moneyPlaceholder = moneyPlaceholderOf(units);
  const [form] = Form.useForm<{
    cancellationAmount?: number;
    cancellationDate?: Dayjs;
    /** Storno-Art: Rückerstattung oder Rücklastschrift. */
    art?: number;
    /** Tenant-Grund-Id oder "custom" (nur bei Rückerstattung). */
    grund?: string;
    /** SEPA-Rückgabecode (nur bei Rücklastschrift). */
    chargebackReasonCode?: string;
    customReason?: string;
    openReminderAssistant?: boolean;
    /** Zahlungsart der Rückerstattung (nur bei Rückerstattung relevant). */
    refundMethod?: number;
    chargebackHandlingMode?: 0 | 1 | 2;
    /** Empfänger der Rückerstattungsüberweisung; leer = Bankdaten der Person. */
    payeeName?: string;
    payeeIban?: string;
    payeeBic?: string;
  }>();

  const [availablePayments, setAvailablePayments] = React.useState<PaymentCancellationItem[]>([]);
  const [selectedRowKeys, setSelectedRowKeys] = React.useState<React.Key[]>([]);
  const [selectedPayments, setSelectedPayments] = React.useState<PaymentCancellationItem[]>([]);
  const { dirty: cancellationDirty, initialize: initializeCancellation } = useFormDirty(form);
  const { dirty: selectionDirty, markSaved: markSelectionSaved } = useDirtySnapshot(selectedRowKeys.map(String).sort());
  const [loadingPayments, setLoadingPayments] = React.useState(false);

  const selectedPaymentsTotalAmount = React.useMemo(
    () => selectedPayments.reduce((sum, p) => sum + Number(p.voidableRemaining || 0), 0),
    [selectedPayments]
  );

  const [currentStep, setCurrentStep] = React.useState(0);
  const operation = useFinanceOperation(personId, operationResources);
  const saving = operation.pending;
  // Stornogründe und Zahlarten füllen die Auswahlfelder in Schritt 2. Über Abfragen statt State
  // plus Effekt: Der Ladezustand ist damit abgeleitet statt gesetzt (kein synchrones setState im
  // Effekt-Rumpf), und beide Listen kommen beim zweiten Öffnen aus dem Cache.
  const cancellationReasonsQuery = useQuery({
    queryKey: ["lookups", "payment-cancellation-reasons"],
    queryFn: async () =>
      ((await auth.api.get<Array<{ id: string; name: string; reasonType: number; isActive: boolean }>>(
        "/api/v1/payment-cancellation-reasons/available"
      )) || [])
        .filter((r) => r.isActive)
        .map((r) => ({ id: r.id, name: r.name, reasonType: Number(r.reasonType ?? 0) })),
    enabled: open
  });
  const paymentMethodDefsQuery = useQuery({
    queryKey: ["lookups", "payment-method-definitions"],
    queryFn: async () =>
      (await auth.api.get<Array<{ paymentMethodType: number; name: string; isActive?: boolean }>>(
        "/api/v1/payment-method-definitions"
      )) || [],
    enabled: open
  });
  const cancellationReasons: CancellationReason[] = React.useMemo(
    () => cancellationReasonsQuery.data ?? [],
    [cancellationReasonsQuery.data]
  );
  const paymentMethodDefs = React.useMemo(
    () => paymentMethodDefsQuery.data ?? [],
    [paymentMethodDefsQuery.data]
  );
  const lookupsLoading = open && (cancellationReasonsQuery.isPending || paymentMethodDefsQuery.isPending);
  const paymentMethodOptions = React.useMemo(
    () => paymentMethodDefinitionSelectOptions(paymentMethodDefs, true),
    [paymentMethodDefs]
  );

  const watchedArt = Form.useWatch("art", form);
  const watchedGrund = Form.useWatch("grund", form);
  const watchedChargebackReasonCode = Form.useWatch("chargebackReasonCode", form);
  const watchedAmount = Form.useWatch("cancellationAmount", form);
  const watchedCancellationDate = Form.useWatch("cancellationDate", form);
  const watchedOpenReminderAssistant = Form.useWatch("openReminderAssistant", form);
  const watchedCustomReason = Form.useWatch("customReason", form);
  const watchedRefundMethod = Form.useWatch("refundMethod", form);
  const watchedChargebackHandlingMode = Form.useWatch("chargebackHandlingMode", form);

  // Im Kontoauszug festgelegt, sonst über die Stornoart gewählt.
  const isChargebackSelected = chargebackOnly || watchedArt === CANCELLATION_REASON_TYPE_CHARGEBACK;
  // Erstattung per Überweisung: Daraus entsteht ein Überweisungsauftrag für die Online-Anbindung.
  const refundByTransfer = !isChargebackSelected && Number(watchedRefundMethod) === PAYMENT_METHOD_TRANSFER;
  const personBankQuery = useQuery({
    queryKey: ["persons", personId, "refund-payee"],
    enabled: open && refundByTransfer,
    queryFn: () => auth.api.get<{ firstName?: string | null; lastName?: string | null; bankAccountHolder?: string | null; iban?: string | null; bic?: string | null }>(
      `/api/v1/persons/${encodeURIComponent(personId)}`)
  });
  const personBank = personBankQuery.data;
  const isCustomSelected = !isChargebackSelected && watchedGrund === CUSTOM_REASON_ID;
  const chargebackBlocksFutureDebits = chargebackReasonBlocksFutureDebits(watchedChargebackReasonCode);

  React.useEffect(() => {
    if (chargebackBlocksFutureDebits) form.setFieldValue("chargebackHandlingMode", undefined);
  }, [chargebackBlocksFutureDebits, form]);

  /** Aufgelöster tenant-Grund, falls einer gewählt ist (kein Freitext). */
  const selectedReasonObj = React.useMemo(() => {
    if (typeof watchedGrund === "string" && watchedGrund && watchedGrund !== CUSTOM_REASON_ID) {
      return cancellationReasons.find((r) => r.id === watchedGrund) ?? null;
    }
    return null;
  }, [watchedGrund, cancellationReasons]);

  // Zahlungen: beim Öffnen immer alle (nicht stornierten) laden; Props nur zur Vorauswahl nutzen
  React.useEffect(() => {
    if (!open) {
      setAvailablePayments([]);
      setSelectedRowKeys([]);
      setSelectedPayments([]);
      return;
    }
    // Bankdatum, Rückgabegrund und Bankbetrag stehen auch ohne erkannte Person bereits fest.
    initializeCancellation({ ...seedValues(initialValues, chargebackOnly), openReminderAssistant: false });
    markSelectionSaved([]);
    // Ohne Person (Kontoauszug: Mitglied noch nicht gewählt) gibt es keine Zahlungen zu laden.
    if (!personId) {
      setAvailablePayments([]);
      setSelectedRowKeys([]);
      setSelectedPayments([]);
      return;
    }
    let active = true;
    const preselectedIds = new Set(
      (paymentsProp && paymentsProp.length > 0 ? paymentsProp : paymentProp ? [paymentProp] : []).map((p) => p.id)
    );
    setLoadingPayments(true);
    auth.api
      .get<ApiPaymentRow[]>(`/api/v1/persons/${encodeURIComponent(personId)}/payments`)
      .then((list) => {
        if (!active) return;
        const isVoidable = (r: ApiPaymentRow) => {
          if (r.rowKind === ROW_KIND_DEBT_COLLECTION) return false;
          if (r.canVoid === true) return true;
          if (r.canVoid === false) return false;
          const gross = Number(r.amount || 0);
          const isReversal = !!(r.reversesPaymentId && gross < 0);
          return !r.isCancelled && !isReversal && gross > 0;
        };
        const items: PaymentCancellationItem[] = (list || [])
          .filter(isVoidable)
          .map((r) => {
            const gross = Number(r.amount || 0);
            const rem =
              r.voidableRemaining != null && r.voidableRemaining !== undefined
                ? Number(r.voidableRemaining)
                : gross;
            return {
              id: r.id,
              voidableRemaining: rem,
              originalAmount: gross,
              paidDate: r.paidDate ?? null,
              method: r.method != null ? String(r.method) : null,
              reference: r.reference ?? null
            };
          });
        setAvailablePayments(items);
        if (preselectedIds.size > 0) {
          const pre = items.filter((r) => preselectedIds.has(r.id));
          markSelectionSaved(pre.map(item => item.id).sort());
          initializeCancellation({ ...seedValues(initialValues, chargebackOnly), openReminderAssistant: false,
            cancellationAmount: initialValues?.cancellationAmount ?? pre.reduce((sum, item) => sum + Number(item.voidableRemaining || 0), 0),
            refundMethod: pre.length > 0 && Number.isFinite(Number(pre[0].method)) ? Number(pre[0].method) : undefined });
          setSelectedRowKeys(pre.map((p) => p.id));
          setSelectedPayments(pre);
        } else {
          markSelectionSaved([]);
          initializeCancellation({ ...seedValues(initialValues, chargebackOnly), openReminderAssistant: false });
          setSelectedRowKeys([]);
          setSelectedPayments([]);
        }
      })
      .catch(() => { if (active) setAvailablePayments([]); })
      .finally(() => { if (active) setLoadingPayments(false); });
    return () => { active = false; };
    // A selection is seeded once per opening; parent reloads must not replace an active draft.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, personId, chargebackOnly, auth.api, initializeCancellation, markSelectionSaved]);

  /** Kompakte Auswahl: Datum und Betrag bleiben schmal, Referenz ist der Hauptinhalt. */
  const selectedPaymentsColumns: ListColumn<PaymentCancellationItem>[] = React.useMemo(
    () => [
      {
        title: text("Payment date"),
        dataIndex: "paidDate",
        key: "paidDate",
        width: DATE_COLUMN_WIDTH,
        render: (_: unknown, r: PaymentCancellationItem) =>
          <span style={{ whiteSpace: "nowrap" }}>
            {r.paidDate ? formatDateOnly(String(r.paidDate).slice(0, ISO_DATE_LENGTH), units) : text("—")}
          </span>
      },
      {
        title: text("Reference"),
        dataIndex: "reference",
        key: "reference",
        width: REFERENCE_COLUMN_WIDTH,
        ellipsis: true,
        render: (_: unknown, r: PaymentCancellationItem) => r.reference ?? text("—")
      },
      {
        title: text(chargebackOnly ? "Available payment amount" : "Voidable remaining"),
        dataIndex: "voidableRemaining",
        key: "voidableRemaining",
        width: AMOUNT_COLUMN_WIDTH,
        align: "right" as const,
        render: (_: unknown, r: PaymentCancellationItem) => (
          <span style={{ whiteSpace: "nowrap" }}>{formatMoney(r.voidableRemaining, units)}</span>
        )
      }
    ],
    [chargebackOnly, text, units]
  );

  // Reset/Vorbelegung beim Öffnen/Schließen bzw. bei Änderung der Auswahl
  React.useEffect(() => {
    if (!open) {
      form.resetFields();
      setCurrentStep(0);
    } else if (selectedPayments.length > 0) {
      form.setFieldsValue({
        cancellationAmount: form.getFieldValue("cancellationAmount") ?? selectedPaymentsTotalAmount,
        cancellationDate: form.getFieldValue("cancellationDate") ?? dayjs().startOf("day"),
        openReminderAssistant: false,
      });
      // Zahlungsart der Rückerstattung mit der Zahlart der Originalzahlung vorbelegen (überschreibbar).
      if (form.getFieldValue("refundMethod") == null) {
        const originalMethod = Number(selectedPayments[0]?.method);
        if (Number.isFinite(originalMethod)) form.setFieldsValue({ refundMethod: originalMethod });
      }
    }
  }, [open, form, selectedPayments, selectedPaymentsTotalAmount]);

  /** Gründe für Rückerstattungen; Rücklastschriften nutzen den separaten SEPA-Rückgabecode-Katalog. */
  const grundOptions = React.useMemo(() => {
    return [
      ...cancellationReasons
        .filter((r) => r.reasonType !== CANCELLATION_REASON_TYPE_CHARGEBACK)
        .map((r) => ({ value: r.id, label: r.name })),
      { value: CUSTOM_REASON_ID, label: text("Custom reason") }
    ];
  }, [cancellationReasons, text]);

  const steps = React.useMemo(
    () => [
      { key: "payments" as const, title: text("Payments & amount") },
      { key: "reason" as const, title: text(chargebackOnly ? "Chargeback reason" : "Reason") },
      { key: "summary" as const, title: text("Summary") }
    ],
    [chargebackOnly, text]
  );

  const effectiveStep = Math.min(currentStep, steps.length - 1);
  const currentKey = steps[effectiveStep]?.key ?? "payments";
  const isLastStep = effectiveStep === steps.length - 1;
  const canSubmit = selectedPayments.length > 0;

  const reasonDisplay = isChargebackSelected
    ? chargebackReasonOptions.find((o) => o.value === watchedChargebackReasonCode)?.label ?? watchedChargebackReasonCode ?? ""
    : isCustomSelected
      ? (watchedCustomReason || "").trim()
      : selectedReasonObj?.name ?? "";

  const next = async () => {
    try {
      if (currentKey === "payments") {
        await form.validateFields(["cancellationAmount", "cancellationDate"]);
      } else if (currentKey === "reason") {
        await form.validateFields(["art"]);
        if (isChargebackSelected) {
          await form.validateFields(["chargebackReasonCode"]);
          if (!chargebackBlocksFutureDebits) await form.validateFields(["chargebackHandlingMode"]);
        } else {
          await form.validateFields(["grund"]);
          if (isCustomSelected) await form.validateFields(["customReason"]);
          await form.validateFields(["refundMethod"]);
        }
      }
      setCurrentStep((s) => Math.min(s + 1, steps.length - 1));
    } catch {
      // Validierung fehlgeschlagen – Fehler wird automatisch angezeigt.
    }
  };

  const prev = () => setCurrentStep((s) => Math.max(s - 1, 0));

  const handleFinish = async () => {
    if (selectedPayments.length === 0) return;
    try {
      const values = await form.validateFields();
      const assistantRequested = isChargebackSelected && Boolean(values.openReminderAssistant);
      const cancellationDateIso = formatDateOnlyIso(values.cancellationDate);
      const result = await operation.execute(async (committed) => {

        const grund = values.grund ?? "";
        let paymentCancellationReasonId: string | null = null;
        let cancellationReason: string | null = null;

        if (isChargebackSelected) {
          cancellationReason = chargebackReasonOptions.find((o) => o.value === values.chargebackReasonCode)?.label ?? text("Chargeback");
        } else if (grund === CUSTOM_REASON_ID) {
          cancellationReason = (values.customReason || "").trim() || null;
        } else if (grund) {
          paymentCancellationReasonId = grund;
          cancellationReason = cancellationReasons.find((r) => r.id === grund)?.name ?? null;
        }

        // cancellationType wird serverseitig aus dem Grund-Typ abgeleitet (Rücklastschrift-Grund → Rücklastschrift).
        const result = await auth.api.post<PaymentCancellationResult>(`/api/v1/persons/${encodeURIComponent(personId)}/payments/cancel-bulk`, {
          paymentIds: selectedPayments.map((p) => p.id),
          cancellationReason: cancellationReason ?? undefined,
          paymentCancellationReasonId: paymentCancellationReasonId ?? undefined,
          chargebackReasonCode: isChargebackSelected ? values.chargebackReasonCode : undefined,
          cancellationDate: cancellationDateIso ?? undefined,
          cancellationAmount:
            values.cancellationAmount != null ? Number(values.cancellationAmount) : selectedPaymentsTotalAmount,
          // Zahlungsart der Rückerstattung; bei Rücklastschrift irrelevant (Bankrückgabe).
          refundMethod: !isChargebackSelected && values.refundMethod != null ? Number(values.refundMethod) : undefined,
          payeeName: refundByTransfer ? values.payeeName?.trim() || undefined : undefined,
          payeeIban: refundByTransfer && values.payeeIban ? normalizeIban(values.payeeIban) : undefined,
          payeeBic: refundByTransfer ? values.payeeBic?.trim() || undefined : undefined,
          chargebackHandlingMode: isChargebackSelected && !chargebackBlocksFutureDebits
            ? Number(values.chargebackHandlingMode)
            : undefined
        });

        committed();
        feedback.success(text(chargebackOnly ? "Chargeback recorded" : "Payment cancelled."));
        const reminderCases = assistantRequested ? (result.reminderCases ?? []) : [];
        if (assistantRequested && reminderCases.length === 0)
          feedback.info(text("No dunnable open item, reminder assistant skipped"));
        onClose();
        await onSuccess?.({
          ...result,
          reminderCases
        });
      });
      if (result === "writeUncertain") onClose();
    } catch {
      // Central API toast shows error
    }
  };

  const summaryRow = (label: string, value: React.ReactNode) => (
    <div style={{ display: "flex", justifyContent: "space-between", gap: spacing.lg, padding: cssSpacing(spacing.xs, 0) }}>
      <Typography.Text type="secondary">{label}</Typography.Text>
      <Typography.Text strong style={{ textAlign: "right" }}>{value}</Typography.Text>
    </div>
  );

  return (
    <FormDrawer mode="action"
      title={chargebackOnly ? "Chargeback" : "Cancel payment"}
      dirty={cancellationDirty || selectionDirty}
      open={open}
      onCancel={onClose}
      contentLoading={contentLoading || lookupsLoading || loadingPayments}
      hideSubmit
      extra={
        <Space>
          {effectiveStep > 0 && (
            <Button onClick={prev} disabled={saving}>
              {text("Back")}
            </Button>
          )}
          {isLastStep ? (
            <Button type="primary" danger loading={saving} disabled={!canSubmit} onClick={handleFinish}>
              {text(chargebackOnly ? "Bank statement book chargeback" : "Apply payment reversal")}
            </Button>
          ) : (
            <Button type="primary" disabled={effectiveStep === 0 && !canSubmit} onClick={next}>
              {text("Next")}
            </Button>
          )}
        </Space>
      }
    >
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        initialValues={{ art: chargebackOnly ? CANCELLATION_REASON_TYPE_CHARGEBACK : CANCELLATION_REASON_TYPE_REFUND, cancellationDate: dayjs().startOf("day"), openReminderAssistant: false }}
      >
        {header}
        <Steps current={effectiveStep} items={steps.map((s) => ({ title: s.title }))} size="small" style={{ marginBottom: spacing.xl }} />

        {/* Schritt 1: Zahlungen & Betrag */}
        <div style={{ display: currentKey === "payments" ? "block" : "none" }}>
          <DrawerSelectionList<PaymentCancellationItem>
            title={text(chargebackOnly ? "Select returned payments" : "Select payments to cancel")}
            loading={loadingPayments}
            dataSource={availablePayments}
            columns={selectedPaymentsColumns}
            rowKey="id"
            scrollX={560}
            emptyText={<Typography.Text type="secondary">{text(chargebackOnly ? "No payments available for chargeback." : "No payments available to cancel.")}</Typography.Text>}
            rowSelection={{
              selectedRowKeys,
              hideSelectAll: false,
              selectionByRowClick: true,
              onChange: (keys, rows) => {
                setSelectedRowKeys(keys);
                setSelectedPayments(rows);
                const total = rows.reduce((sum, r) => sum + Number(r.voidableRemaining || 0), 0);
                if (rows.length > 0) form.setFieldsValue({
                  cancellationAmount: chargebackOnly && initialValues?.cancellationAmount != null
                    ? initialValues.cancellationAmount : total
                });
              }
            }}
          />

          {isChargebackSelected && selectedPayments.length > 0 && Number(watchedAmount) > selectedPaymentsTotalAmount &&
            summaryRow(text("Chargeback fee"), formatMoney(Number(watchedAmount) - selectedPaymentsTotalAmount, units))}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: stackPaymentFields ? "1fr" : "repeat(2, minmax(180px, 220px))",
              columnGap: spacing.md,
              alignItems: "start"
            }}
          >
            <Form.Item
              name="cancellationAmount"
              label={text(chargebackOnly ? "Chargeback amount" : "Cancelled amount")}
              rules={[
                { required: true, message: text("Please enter an amount") },
                {
                  validator: (_, value) => {
                    if (value == null || value <= 0) {
                      return Promise.reject(new Error(text("Amount must be greater than 0")));
                    }
                    return Promise.resolve();
                  }
                }
              ]}
            >
              <InputNumber
                style={{ width: "100%" }}
                min={0.01}
                step={0.01}
                precision={2}
                decimalSeparator={decimalSeparator}
                addonAfter={(units.currency || units.currencyCode || "").trim() || "EUR"}
                placeholder={moneyPlaceholder}
              />
            </Form.Item>

            <Form.Item
              name="cancellationDate"
              label={text(chargebackOnly ? "Chargeback date" : "Payment cancellation date")}
              rules={[
                { required: true, message: text(chargebackOnly ? "Chargeback date is required" : "Payment cancellation date is required") },
                {
                  validator: (_, value) => {
                    if (!value) return Promise.resolve();
                    const selectedDate = dayjs(value);
                    if (selectedDate.isAfter(dayjs().startOf("day"), "day")) {
                      return Promise.reject(new Error(text(chargebackOnly ? "Chargeback date must not be in the future" : "Payment cancellation date must not be in the future")));
                    }
                    return Promise.resolve();
                  }
                }
              ]}
            >
              <DatePicker
                style={{ width: "100%" }}
                format={dateInputFormat}
                allowClear={false}
                disabledDate={(current) => {
                  if (!current) return false;
                  return current.isAfter(dayjs().endOf("day"), "day");
                }}
              />
            </Form.Item>
          </div>
        </div>

        {/* Schritt 2: Art wählen, dann passenden Grund */}
        <div style={{ display: currentKey === "reason" ? "block" : "none" }}>
          <Form.Item
            name="art"
            hidden={chargebackOnly}
            label={text("Cancellation type")}
            rules={[{ required: true, message: text("Please select a cancellation type") }]}
          >
            <Radio.Group
              optionType="button"
              buttonStyle="solid"
              onChange={() => {
                form.setFieldsValue({ grund: undefined, chargebackReasonCode: undefined, customReason: undefined });
              }}
              options={[
                { value: CANCELLATION_REASON_TYPE_REFUND, label: text("Refund") },
                { value: CANCELLATION_REASON_TYPE_CHARGEBACK, label: text("Chargeback") }
              ]}
            />
          </Form.Item>
          {isChargebackSelected ? (
            <Form.Item
              name="chargebackReasonCode"
              label={text("Chargeback reason")}
              rules={[{ required: true, message: text("Please select a chargeback reason") }]}
            >
              <Select
                showSearch
                optionFilterProp="label"
                placeholder={text("Select chargeback reason...")}
                options={chargebackReasonOptions}
              />
            </Form.Item>
          ) : (
            <Form.Item
              name="grund"
              label={text("Reason")}
              rules={[{ required: true, message: text("Please select a reason") }]}
            >
              <Select
                showSearch
                optionFilterProp="label"
                placeholder={text("Select reason…")}
                options={grundOptions}
              />
            </Form.Item>
          )}
          {isCustomSelected && (
            <Form.Item name="customReason" rules={[{ required: true, message: text("Please enter a reason") }]}>
              <Input.TextArea rows={3} placeholder={text("Enter reason...")} />
            </Form.Item>
          )}
          {!isChargebackSelected && (
            <Form.Item
              name="refundMethod"
              label={text("Refund method")}
              rules={[{ required: true, message: text("Please select a refund method") }, {
                validator: (_, value) => paymentMethodOptions.some(option => option.value === Number(value))
                  ? Promise.resolve() : Promise.reject(new Error(text("Please select a refund method"))),
              }]}
            >
              <Select
                showSearch
                optionFilterProp="label"
                placeholder={text("Select payment method…")}
                options={paymentMethodOptions}
              />
            </Form.Item>
          )}
          {refundByTransfer && <>
            <Form.Item name="payeeName" label={text("Account holder")}>
              <Input maxLength={70} autoComplete="off"
                placeholder={personBank?.bankAccountHolder || [personBank?.firstName, personBank?.lastName].filter(Boolean).join(" ") || undefined} />
            </Form.Item>
            <Form.Item name="payeeIban" label={text("IBAN")} rules={[
              { required: !personBankQuery.isPending && !personBank?.iban, message: text("IBAN is required") },
              { validator: (_, value) => !value || isValidIban(String(value)) ? Promise.resolve() : Promise.reject(new Error(text("Invalid IBAN"))) }
            ]}>
              <Input maxLength={42} autoComplete="off" placeholder={personBank?.iban ?? undefined} />
            </Form.Item>
            <Form.Item name="payeeBic" label={text("BIC")}>
              <Input maxLength={11} autoComplete="off" placeholder={personBank?.bic ?? undefined} />
            </Form.Item>
          </>}
          {isChargebackSelected && chargebackBlocksFutureDebits && (
            <Alert type="warning" showIcon message={text("Further direct debits blocked by return reason")} />
          )}
          {isChargebackSelected && !chargebackBlocksFutureDebits && (
            <Form.Item
              name="chargebackHandlingMode"
              label={text("Further handling after chargeback")}
              rules={[{ required: true, message: text("Please select further handling after chargeback") }]}
            >
              <Select options={[
                { value: 0, label: text("Include in next regular SEPA run") },
                { value: 1, label: text("Block SEPA until contribution is paid") },
                { value: 2, label: text("Block returned items only; continue collecting future contributions") }
              ]} />
            </Form.Item>
          )}
          {isChargebackSelected && (
            <Form.Item
              name="openReminderAssistant"
              valuePropName="checked"
            >
              <Checkbox>{text("Open reminder assistant afterwards")}</Checkbox>
            </Form.Item>
          )}
        </div>

        {/* Schritt 3: Übersicht */}
        <div style={{ display: currentKey === "summary" ? "block" : "none" }}>
          <Typography.Title level={5} style={{ marginTop: 0 }}>{text("Summary")}</Typography.Title>
          {summaryRow(
            text("Payments"),
            text("Selected payments count").replace("{0}", String(selectedPayments.length))
          )}
          {summaryRow(text(chargebackOnly ? "Chargeback amount" : "Cancelled amount"), formatMoney(Number(watchedAmount || 0), units))}
          {isChargebackSelected && Number(watchedAmount) > selectedPaymentsTotalAmount &&
            summaryRow(text("Chargeback fee"), formatMoney(Number(watchedAmount) - selectedPaymentsTotalAmount, units))}
          {summaryRow(
            text(chargebackOnly ? "Chargeback date" : "Payment cancellation date"),
            watchedCancellationDate ? formatDateOnly(formatDateOnlyIso(watchedCancellationDate) ?? "", units) : text("—")
          )}
          {!chargebackOnly && summaryRow(text("Cancellation type"), isChargebackSelected ? text("Chargeback") : text("Refund"))}
          {summaryRow(text(chargebackOnly ? "Chargeback reason" : "Reason"), reasonDisplay || text("—"))}
          {!isChargebackSelected && summaryRow(
            text("Refund method"),
            watchedRefundMethod != null
              ? (paymentMethodOptions.find((o) => o.value === Number(watchedRefundMethod))?.label
                ?? paymentMethodLabel(watchedRefundMethod))
              : text("—")
          )}
          {isChargebackSelected && summaryRow(
            text("Further handling after chargeback"),
            chargebackBlocksFutureDebits
              ? text("Further direct debits blocked by return reason")
              : watchedChargebackHandlingMode === CHARGEBACK_HANDLING_NEXT_RUN
                ? text("Include in next regular SEPA run")
                : watchedChargebackHandlingMode === CHARGEBACK_HANDLING_BLOCK_UNTIL_PAID
                  ? text("Block SEPA until contribution is paid")
                  : watchedChargebackHandlingMode === CHARGEBACK_HANDLING_BLOCK_RETURNED_ONLY
                    ? text("Block returned items only; continue collecting future contributions")
                    : text("—")
          )}
          {isChargebackSelected && summaryRow(
            text("Open reminder assistant afterwards"),
            watchedOpenReminderAssistant ? text("Yes") : text("No")
          )}
        </div>
      </Form>
    </FormDrawer>
  );
};
